<script setup>
import { computed, inject, markRaw, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import { useHeightComposable } from '@/composables/useHeightComposable'

import HeadLineTab from '@/components/headline/HeadLineTab.vue'
import PhreaticLineTab from '@/components/phreaticline/PhreaticLineTab.vue'
import SoilProfileBlocksTab from '@/components/soilcomposition/SoilProfileBlocksTab.vue'
import SoilProfileWedgesTab from '@/components/soil-profile-wedges/SoilProfileWedgesTab.vue'
import StatesTab from '@/components/state/StatesTab.vue'
import TopoProfileTab from '@/components/topoprofile/TopoProfileTab.vue'
import OverviewTab from '@/components/overview/OverviewTab.vue'
import LoadsTab from '@/components/loads/LoadsTab.vue'
import ReinforcementTab from '@/components/reinforcement/ReinforcementTab.vue'
import SchematisationStageSelect from '@/components/schematisationDetail/SchematisationStageSelect.vue'

const store = useStore()
const router = useRouter()

const appHeaderHeight = inject('appHeaderHeight')
const tab = ref('')

const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)
const currentCompositionId = computed(
  () => store.getters['stabSchematisationStage/currentCompositionId']
)
const currentSoilTypeRevisionId = computed(
  () => store.getters['stabSchematisationStage/currentSoilTypeRevisionId']
)

const tabsRef = ref(null)
const tabsHeight = ref(0)
const topHeight = computed(() => {
  return (appHeaderHeight?.value ?? 0) + tabsHeight.value
})
provide('schemaTabHeight', tabsHeight)
provide('topHeight', topHeight)
const { mountObserver, unmountObserver } = useHeightComposable(tabsHeight, tabsRef)

watch(
  () => currentStabSchematisationStage.value,
  async () => {
    assignFirstTab()
    await fetchItemsPage()
  },
  { deep: true }
)

watch(
  () => tab.value,
  () => {
    if (tab.value) {
      router.replace({ query: { tab: tab.value } })
    }
  }
)

const fetchOptions = async () => await store.dispatch('topoProfilePoint/fetchOptions')
const fetchItems = async (id) => await store.dispatch('topoProfilePoint/fetchItems', id)

const fetchItem = async (id) => await store.dispatch('topoProfile/fetchItem', id)
const fetchSoilProfileItems = async (id) => await store.dispatch('soilProfile/fetchItems', id)
const fetchSoilProfilePointItems = async (id) =>
  await store.dispatch('soilProfilePoint/fetchSoilProfilePoints', id)

const fetchSoilLineItems = async (id) => await store.dispatch('soilLine/fetchItems', id)
const fetchSoilLayerItems = async (id) => await store.dispatch('soilLayer/fetchItems', id)
const fetchItemsAdd = async (id) => await store.dispatch('soilType/fetchItemsAdd', id)
const fetchSoilTypesOverview = async (id) => await store.dispatch('soilType/fetchItemsOverview', id)

const fetchStates = async (id) => await store.dispatch('statePoints/fetchItems', id)

const assignFirstTab = (urlTab = null) => {
  if (urlTab) {
    tab.value = urlTab
    return
  }
  const stageTab = [
    { topo_profile: 'topo-profile' },
    { soil_profile: 'soil-profile-block' },
    { phreatic_line: 'phreatic-line' },
    { head_line: 'head-line' },
    { states: 'state-point' },
    { loads: 'load' },
    { reinforcement: 'reinforcement' },
    { overview: 'overview' }
  ]
  for (const [key, value] of Object.entries(stageTab)) {
    if (key === 'phreatic_line' || key === 'head_line') {
      if (currentStabSchematisationStage.value.stage_configuration[key] !== 'no') {
        tab.value = value
        return
      }
    }
    if (currentStabSchematisationStage.value.stage_configuration[key]) {
      tab.value = value
      return
    }
  }
}

const fetchItemsPage = async () => {
  try {
    if (currentStabSchematisationStage.value.topoprofile) {
      await fetchItem(currentStabSchematisationStage.value.topoprofile)
      await fetchItems(currentStabSchematisationStage.value.topoprofile)
    }
    await fetchRelatedSoilItems()
  } catch (e) {
    console.log(e)
  }
}
const onTabChanged = () => {
  // Used to make sure that the chart will always fill the full window
  window.dispatchEvent(new Event('resize'))
}
const fetchRelatedSoilItems = async () => {
  await fetchSoilProfileItems(currentCompositionId.value)
  await fetchSoilProfilePointItems(currentCompositionId.value)
  await fetchSoilLineItems(currentCompositionId.value)
  await fetchSoilLayerItems(currentCompositionId.value)
  await fetchStates(currentCompositionId.value)
}
const fetchRelatedSoilTypes = async () => {
  await fetchItemsAdd(currentSoilTypeRevisionId.value)
  await fetchSoilTypesOverview(currentSoilTypeRevisionId.value)
}

const currentStageConfiguration = computed(() => {
  return currentStabSchematisationStage.value.stage_configuration
})

const tabs = computed(() => {
  return [
    {
      value: 'topo-profile',
      label: 'Topographical profile',
      component: markRaw(TopoProfileTab),
      isShow: currentStageConfiguration.value.topo_profile
    },
    {
      value: 'soil-profile-block',
      label: 'Soil profile blocks',
      component: markRaw(SoilProfileBlocksTab),
      isShow: currentStageConfiguration.value.soil_profile
    },
    {
      value: 'soil-profile-wedge',
      label: 'Soil profile wedges',
      component: markRaw(SoilProfileWedgesTab),
      isShow: currentStageConfiguration.value.soil_profile
    },
    {
      value: 'phreatic-line',
      label: 'Phreatic line',
      component: markRaw(PhreaticLineTab),
      isShow: currentStageConfiguration.value.phreatic_line !== 'no'
    },
    {
      value: 'head-line',
      label: 'Head line',
      component: markRaw(HeadLineTab),
      isShow: currentStageConfiguration.value.head_line_1 !== 'no'
    },
    {
      value: 'state-point',
      label: 'States',
      component: markRaw(StatesTab),
      isShow: currentStageConfiguration.value.states
    },
    {
      value: 'load',
      label: 'Loads',
      component: markRaw(LoadsTab),
      isShow: currentStageConfiguration.value.loads
    },
    {
      value: 'reinforcement',
      label: 'Reinforcements',
      component: markRaw(ReinforcementTab),
      isShow: currentStageConfiguration.value.reinforcement
    },
    {
      value: 'overview',
      label: 'Overview',
      component: markRaw(OverviewTab),
      isShow: currentStageConfiguration.value.overview
    }
  ]
})

const ignoreHeight = computed(() => {
  return appHeaderHeight?.value + tabsHeight.value + 'px'
})

onMounted(async () => {
  await mountObserver()
  const currentTab = router.currentRoute.value.query.tab ?? null
  assignFirstTab(currentTab)
  await fetchOptions()
  await fetchItemsPage()
  await fetchRelatedSoilTypes()
})

onUnmounted(() => {
  unmountObserver()
})
</script>

<template>
  <v-container ref="tabsRef" class="tabs-container">
    <SchematisationStageSelect class="left-section" />
    <v-tabs v-model="tab" color="primary" grow height="64" @update:model-value="onTabChanged">
      <template v-for="item in tabs" :key="item.value">
        <v-tab v-if="item.isShow" :value="item.value" rounded="0">
          {{ item.label }}
        </v-tab>
      </template>
    </v-tabs>
  </v-container>
  <v-window v-model="tab" v-if="currentStabSchematisationStage.id">
    <template v-for="item in tabs" :key="item.value">
      <v-window-item
        class="fill-height"
        v-if="item.isShow"
        :value="item.value"
        :key="`tab-item-${item.value}-${currentStabSchematisationStage.id}`"
      >
        <component :is="item.component" />
      </v-window-item>
    </template>
  </v-window>
</template>

<style scoped>
.v-window {
  height: calc(100vh - v-bind(ignoreHeight));
  margin: 0;
}

.tabs-container {
  display: flex;
  align-items: flex-start; /* Align items at the start of the flex container */
}

.left-section {
  min-width: 12vw;
  max-width: 20vw;
}

.v-container {
  padding: 0;
  margin: 0;
  min-width: 100%;
}
</style>
